import { Table, Badge, Group, Text, ActionIcon, Skeleton } from '@mantine/core'
import { IconExternalLink, IconScale, IconTrash, IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import type { Service } from '@/types'
import { formatDistanceToNow } from 'date-fns'

interface ServiceTableProps {
  services: Service[]
  loading?: boolean
}

export function ServiceTable({ services, loading }: ServiceTableProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Image</Table.Th>
            <Table.Th>Replicas</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Ports</Table.Th>
            <Table.Th>Updated</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {[...Array(5)].map((_, i) => (
            <Table.Tr key={i}>
              <Table.Td><Skeleton height={20} width={120} /></Table.Td>
              <Table.Td><Skeleton height={20} width={200} /></Table.Td>
              <Table.Td><Skeleton height={20} width={60} /></Table.Td>
              <Table.Td><Skeleton height={20} width={80} /></Table.Td>
              <Table.Td><Skeleton height={20} width={100} /></Table.Td>
              <Table.Td><Skeleton height={20} width={100} /></Table.Td>
              <Table.Td><Skeleton height={20} width={80} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  const rows = services.map((service) => {
    const isGlobal = !!service.Spec.Mode.Global
    const replicas = service.Spec.Mode.Replicated?.Replicas || 0
    const ports = service.Endpoint?.Spec?.Ports || []

    return (
      <Table.Tr key={service.ID}>
        <Table.Td>
          <Text fw={500}>{service.Spec.Name}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c="dimmed" style={{ fontFamily: 'monospace' }}>
            {service.Spec.TaskTemplate.ContainerSpec.Image}
          </Text>
        </Table.Td>
        <Table.Td>
          {isGlobal ? (
            <Badge color="blue" variant="light">Global</Badge>
          ) : (
            <Text>{replicas}</Text>
          )}
        </Table.Td>
        <Table.Td>
          <Badge color="green" variant="dot">
            Running
          </Badge>
        </Table.Td>
        <Table.Td>
          {ports.length > 0 ? (
            <Group gap="xs">
              {ports.map((port) => (
                <Badge key={port.PublishedPort} variant="outline" size="sm">
                  {port.PublishedPort}:{port.TargetPort}
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">-</Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm" c="dimmed">
            {formatDistanceToNow(new Date(service.UpdatedAt), { addSuffix: true })}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap="xs" justify="flex-end">
            <ActionIcon
              variant="subtle"
              onClick={() => navigate(`/services/${service.ID}`)}
              title="View details"
            >
              <IconExternalLink size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="blue"
              title="Scale service"
            >
              <IconScale size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="orange"
              title="Pause service"
            >
              <IconPlayerPause size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              title="Delete service"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <Table horizontalSpacing="md" verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Image</Table.Th>
          <Table.Th>Replicas</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Ports</Table.Th>
          <Table.Th>Updated</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  )
}